import { useEffect, useMemo, useState } from "react";
import {
  Button,
  Checkbox,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  AttachMoney,
  Check,
  EditCalendar,
  InfoOutlined,
  Search as SearchIcon,
  WhatsApp,
} from "@mui/icons-material";
import dayjs from "dayjs";
import { useAppointmentStats } from "@/services/fhir/statistics/hooks/useAppointmentStats";
import { CircularLoading } from "@/components/common";
import { CommonDialog } from "@/components/common/CommonDialog";
import ScheduleRescheduleDialog from "@/components/schedule/ScheduleRescheduleDialog";
import { BatchRescheduleDialog } from "@/components/schedule/BatchRescheduleDialog";
import { buildRescheduleEntries } from "@/components/schedule/batchRescheduleUtils";
import { PaymentPendingModal } from "@/components/common/PaymentPendingModal";
import { marketplaceService } from "@/services/marketplace/MarketplaceService";

import { useSessionContext } from "@/contexts/SessionContext";
import { buildCobrancaConsultaNotification } from "@/services/notification/notificationHelpers";
import { notificationService } from "@/services/notification/PostNotification";
import { toast } from "react-toastify";
import { EditAppointment } from "@/services/fhir";
import { useKeepPaymentModalOpenAfterCharge } from "@/hooks/useKeepPaymentModalOpenAfterCharge";
import {
  isChargeNotificationSentOnAppointment,
  markAppointmentChargeNotificationSent,
} from "@/utils/chargeNotificationSent";
import { SendWhatsAppMessageDialog } from "src-next/modules/crm-clinical/messaging";
import type { PaymentDetailData } from "../types/consultationsBilling.types";

import {
  isActionablePendingAppointment,
  isExpiredPendingAppointment,
  isOverdueBookedAppointment,
  getAppointmentSubCaseFilterOptions,
  matchesAppointmentSubCaseFilter,
  type AppointmentSubCaseFilter,
  getAppointmentSituationMeta,
  hasMultipleAppointmentSituations,
  isAppointmentCrmSituationEnabled,
} from "../utils/consultationSituationLabels";
import {
  canRescheduleAppointment,
  resolvePaymentDetailForPendingAppointment,
  resolvePaymentDetailsForAppointments,
} from "../utils/matchConsultationToPayment";
import { sumPaymentDetailsAmount, formatStatsCurrency } from "../utils/getChargeAmount";
import {
  comparePtBrStrings,
  ConsultationSituationCell,
  StatsSortableTableHeadCell,
  statsTableActionButtonSx,
  statsTableActionIconSx,
  statsTableCellTextSx,
  statsTableContainerSx,
  statsTableDateCellSx,
  statsTableDateTextSx,
  statsTableHeadCellSx,
  statsTablePatientCellSx,
  statsTableSituationCellSx,
  statsTableSx,
  statsTableTimeCellSx,
  statsTableTimeTextSx,
  statsTableTruncatedCellSx,
  useStatsTableSort,
} from "../utils/statsTableStyles";
import {
  mapAppointmentRowsToMessagingPatientEntries,
  mapAppointmentToMessagingAppointment,
  mapFhirPatientToMessagingPatient,
  summarizeMessagingRecipients,
  useConsultationWhatsAppSend,
} from "../utils/mapConsultationToWhatsApp";
import { useManagerContext } from "@/contexts/ManagerContext";

interface PreloadedAppointmentStats {
  appointments: fhir5.Appointment[];
  patients: fhir5.Patient[];
}

interface ConsultationsActionTableProps {
  status: string[];
  dateRange?: {
    startDate?: string;
    endDate?: string;
  };
  actors: string[];
  preloadedStats?: PreloadedAppointmentStats;
  onAppointmentsChanged?: () => void;
  showAllActionIcons?: boolean;
  chargeActionsEnabled?: boolean;
  pendingChargePayments?: PaymentDetailData[];
  chargePaymentSlots?: fhir5.Slot[];
  onBatchSendCharges?: (payments: PaymentDetailData[]) => void;
  loadingBatchCharge?: boolean;
}

interface AppointmentTableRow {
  appointment: fhir5.Appointment;
  date: string;
  time: string;
  patientName: string;
  location: string;
  service: string;
  practitioner: string;
  practitionerKey: string;
  locationKey: string;
}

type AppointmentSortField =
  | "date"
  | "patient"
  | "situation"
  | "location"
  | "service"
  | "practitioner";

function getPractitionerKey(appointment: fhir5.Appointment) {
  const participant = appointment.participant?.find((entry) =>
    entry.actor?.reference?.startsWith("Practitioner/")
  );
  return participant?.actor?.reference?.split("/")[1] || "sem-profissional";
}

function getLocationKey(appointment: fhir5.Appointment) {
  const participant = appointment.participant?.find((entry) =>
    entry.actor?.reference?.startsWith("Location/")
  );
  return participant?.actor?.reference?.split("/")[1] || "sem-local";
}

function BatchChargeConfirmDialog({
  open,
  onClose,
  onConfirm,
  loading = false,
  totalSelected,
  chargeableCount,
  chargeableTotal,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  loading?: boolean;
  totalSelected: number;
  chargeableCount: number;
  chargeableTotal: number;
}) {
  const skippedCount = Math.max(0, totalSelected - chargeableCount);

  return (
    <CommonDialog isOpen={open} handleClose={onClose} maxWidth="xs">
      <Stack spacing={2.5} sx={{ p: 2.5, minWidth: { xs: "100%", sm: 360 } }}>
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          <InfoOutlined sx={{ color: "info.main", mt: 0.25 }} />
          <Stack spacing={0.75}>
            <Typography variant="h6" fontWeight={700}>
              Enviar cobranças
            </Typography>
            <Typography variant="body2" color="text.secondary" lineHeight={1.5}>
              {totalSelected}{" "}
              {totalSelected === 1 ? "consulta na agenda" : "consultas na agenda"}
              .{" "}
              <strong>
                {chargeableCount}{" "}
                {chargeableCount === 1 ? "vai receber" : "vão receber"}
              </strong>{" "}
              {chargeableCount === 1 ? "cobrança" : "cobranças"}
              {chargeableCount > 0 ? (
                <>
                  , totalizando{" "}
                  <strong>{formatStatsCurrency(chargeableTotal)}</strong>
                </>
              ) : (
                "."
              )}
              {chargeableCount > 0 ? "." : null}
            </Typography>
            {skippedCount > 0 && (
              <Typography variant="body2" color="text.secondary" lineHeight={1.5}>
                As outras já estão pagas ou confirmadas.
              </Typography>
            )}
          </Stack>
        </Stack>
        <Stack direction="row" spacing={1.5} justifyContent="flex-end">
          <Button variant="outlined" onClick={onClose} disabled={loading}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            startIcon={<AttachMoney />}
            disabled={loading || chargeableCount === 0}
            onClick={onConfirm}
          >
            Enviar {chargeableCount}{" "}
            {chargeableCount === 1 ? "cobrança" : "cobranças"}
          </Button>
        </Stack>
      </Stack>
    </CommonDialog>
  );
}

function BatchWhatsAppDuplicateConfirmDialog({
  open,
  onClose,
  onConfirm,
  totalEntries,
  uniqueCount,
  duplicateEntries,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  totalEntries: number;
  uniqueCount: number;
  duplicateEntries: number;
}) {
  return (
    <CommonDialog isOpen={open} handleClose={onClose} maxWidth="xs">
      <Stack spacing={2.5} sx={{ p: 2.5, minWidth: { xs: "100%", sm: 360 } }}>
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          <InfoOutlined sx={{ color: "info.main", mt: 0.25 }} />
          <Stack spacing={0.75}>
            <Typography variant="h6" fontWeight={700}>
              Mesmo paciente em várias linhas
            </Typography>
            <Typography variant="body2" color="text.secondary" lineHeight={1.5}>
              Você marcou {totalEntries}{" "}
              {totalEntries === 1 ? "linha" : "linhas"}, mas só{" "}
              <strong>
                {uniqueCount} {uniqueCount === 1 ? "pessoa" : "pessoas"}
              </strong>{" "}
              {uniqueCount === 1 ? "vai receber" : "vão receber"} WhatsApp.
            </Typography>
            {duplicateEntries > 0 && (
              <Typography variant="body2" color="text.secondary" lineHeight={1.5}>
                Repetições não geram mensagem extra. Cada paciente recebe só uma.
              </Typography>
            )}
          </Stack>
        </Stack>
        <Stack direction="row" spacing={1.5} justifyContent="flex-end">
          <Button variant="outlined" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            startIcon={<WhatsApp />}
            disabled={uniqueCount === 0}
            onClick={onConfirm}
          >
            Enviar {uniqueCount}{" "}
            {uniqueCount === 1 ? "mensagem" : "mensagens"}
          </Button>
        </Stack>
      </Stack>
    </CommonDialog>
  );
}

export function ConsultationsActionTable({
  status,
  dateRange,
  actors,
  preloadedStats,
  onAppointmentsChanged,
  showAllActionIcons = false,
  chargeActionsEnabled = true,
  pendingChargePayments = [],
  chargePaymentSlots = [],
  onBatchSendCharges,
  loadingBatchCharge = false,
}: ConsultationsActionTableProps) {
  const { displaySchedules } = useManagerContext();
  const { access } = useSessionContext();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(8);
  const [searchQuery, setSearchQuery] = useState("");
  const [practitionerFilter, setPractitionerFilter] = useState("all");
  const [locationFilter, setLocationFilter] = useState("all");
  const [subCaseFilter, setSubCaseFilter] =
    useState<AppointmentSubCaseFilter>("all");
  const [selectedAppointmentIds, setSelectedAppointmentIds] = useState<
    Set<string>
  >(new Set());
  const [chargeConfirmOpen, setChargeConfirmOpen] = useState(false);
  const [whatsAppDupConfirmOpen, setWhatsAppDupConfirmOpen] = useState(false);
  const [batchRescheduleOpen, setBatchRescheduleOpen] = useState(false);
  const { sortField, sortDirection, requestSort, resetSort } =
    useStatsTableSort<AppointmentSortField>("date");
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState<any>(null);
  const [selectedPaymentNoticeContext, setSelectedPaymentNoticeContext] =
    useState<{
      practitionerName?: string;
      appointmentStart?: string;
      locationLabel?: string;
      patientPhone?: string;
    } | null>(null);
  const [loadingSendCharge, setLoadingSendCharge] = useState(false);
  const [errorSendCharge, setErrorSendCharge] = useState<string | undefined>();
  const [loadingConfirmPayment, setLoadingConfirmPayment] = useState(false);
  const [rescheduleDialogOpen, setRescheduleDialogOpen] = useState(false);
  const [rescheduleAppointment, setRescheduleAppointment] = useState<any>(null);
  const keepOpenAfterCharge = useKeepPaymentModalOpenAfterCharge();
  const {
    openSend: openWhatsAppSend,
    openSendBatch: openWhatsAppSendBatch,
    isTemplatesLoading: isMessagingTemplatesLoading,
    hasTemplates: hasMessagingTemplates,
    sendDialogProps,
  } = useConsultationWhatsAppSend();

  const isOverdueBookedMode = status.includes("overdueBooked");
  const isMarcadasMode = status.includes("marcadas");
  const isClosedMode = status.includes("closed");
  const isFulfilledMode = status.includes("fulfilled");
  const subCaseFilterOptions = getAppointmentSubCaseFilterOptions(status);
  const crmMessagingEnabled = isAppointmentCrmSituationEnabled(status);
  const isUpcomingBookedMode = status.length === 1 && status[0] === "booked";
  const isPendingMode = status.length === 1 && status[0] === "pending";
  const apiStatus =
    isMarcadasMode
      ? ["booked", "pending"]
      : isClosedMode
        ? ["cancelled", "pending"]
        : isOverdueBookedMode || isUpcomingBookedMode
          ? ["booked"]
          : status.filter((entry) => entry !== "overdueBooked");

  const canUsePreloaded =
    preloadedStats && (isMarcadasMode || isOverdueBookedMode);

  const { data: fetchedData, loading: fetchLoading, error, fetchStats } =
    useAppointmentStats({
      actors,
      startDate: dateRange?.startDate,
      endDate: dateRange?.endDate,
      status: apiStatus,
      summary: false,
    });

  const data = canUsePreloaded ? preloadedStats : fetchedData;
  const loading = canUsePreloaded ? false : fetchLoading;

  const refreshRows = () => {
    if (canUsePreloaded) onAppointmentsChanged?.();
    else fetchStats();
  };

  useEffect(() => {
    if (actors.length === 0 || canUsePreloaded) return;
    fetchStats();
    setPage(0);
    setSearchQuery("");
    setPractitionerFilter("all");
    setLocationFilter("all");
    setSubCaseFilter("all");
    setSelectedAppointmentIds(new Set());
    resetSort("date");
  }, [actors, fetchStats, status, dateRange?.startDate, dateRange?.endDate, resetSort]);

  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === "visible" && actors.length > 0 && !canUsePreloaded) {
        fetchStats();
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibility);
  }, [actors, fetchStats]);

  const rows = useMemo<AppointmentTableRow[]>(() => {
    const mapped = (data?.appointments ?? []).map((appointment) => {
        const patient = (data?.patients ?? []).find((p) =>
          appointment.participant?.some(
            (part) => part.actor?.reference === `Patient/${p.id}`
          )
        );

        const locationParticipant = appointment.participant?.find((p) =>
          p.actor?.reference?.startsWith("Location/")
        );

        const service = appointment.participant?.find((p) =>
          p.actor?.reference?.startsWith("HealthcareService/")
        );

        const practitioner = appointment.participant?.find((p) =>
          p.actor?.reference?.startsWith("Practitioner/")
        );

        return {
          appointment,
          date: appointment.start
            ? dayjs(appointment.start).format("DD/MM/YYYY")
            : "—",
          time: appointment.start ? dayjs(appointment.start).format("HH:mm") : "—",
          patientName: patient?.name?.[0]?.text || "Não informado",
          location: locationParticipant?.actor?.display || "—",
          service: service?.actor?.display || "—",
          practitioner: practitioner?.actor?.display || "—",
          practitionerKey: getPractitionerKey(appointment),
          locationKey: getLocationKey(appointment),
        };
      });

    if (isOverdueBookedMode) {
      return mapped.filter((row) =>
        isOverdueBookedAppointment(row.appointment)
      );
    }

    if (isMarcadasMode) {
      return mapped.filter((row) => {
        const { appointment } = row;
        if (appointment.status === "booked") {
          return !isOverdueBookedAppointment(appointment);
        }
        if (appointment.status === "pending") {
          return isActionablePendingAppointment(appointment);
        }
        return false;
      });
    }

    if (isClosedMode) {
      return mapped.filter((row) => {
        const { appointment } = row;
        if (appointment.status === "cancelled") return true;
        if (appointment.status === "pending") {
          return isExpiredPendingAppointment(appointment);
        }
        return false;
      });
    }

    if (isUpcomingBookedMode) {
      return mapped.filter(
        (row) => !isOverdueBookedAppointment(row.appointment)
      );
    }

    if (isPendingMode) {
      return mapped.filter((row) =>
        isActionablePendingAppointment(row.appointment)
      );
    }

    return mapped;
  }, [
    data,
    isOverdueBookedMode,
    isMarcadasMode,
    isClosedMode,
    isUpcomingBookedMode,
    isPendingMode,
  ]);

  const practitionerOptions = useMemo(() => {
    const options = new Map<string, string>();
    rows.forEach((row) => {
      options.set(row.practitionerKey, row.practitioner);
    });
    return Array.from(options.entries()).sort((a, b) =>
      a[1].localeCompare(b[1], "pt-BR")
    );
  }, [rows]);

  const locationOptions = useMemo(() => {
    const options = new Map<string, string>();
    rows.forEach((row) => {
      options.set(row.locationKey, row.location);
    });
    return Array.from(options.entries()).sort((a, b) =>
      a[1].localeCompare(b[1], "pt-BR")
    );
  }, [rows]);

  const filteredRows = useMemo(() => {
    let nextRows = rows;

    if (practitionerFilter !== "all") {
      nextRows = nextRows.filter((row) => row.practitionerKey === practitionerFilter);
    }

    if (locationFilter !== "all") {
      nextRows = nextRows.filter((row) => row.locationKey === locationFilter);
    }

    if (subCaseFilter !== "all") {
      nextRows = nextRows.filter((row) =>
        matchesAppointmentSubCaseFilter(row.appointment, subCaseFilter)
      );
    }

    const query = searchQuery.trim().toLowerCase();
    if (query) {
      nextRows = nextRows.filter((row) => {
        const patient = row.patientName.toLowerCase();
        const practitioner = row.practitioner.toLowerCase();
        const location = row.location.toLowerCase();
        const service = row.service.toLowerCase();
        return (
          patient.includes(query) ||
          practitioner.includes(query) ||
          location.includes(query) ||
          service.includes(query)
        );
      });
    }

    return nextRows;
  }, [rows, practitionerFilter, locationFilter, subCaseFilter, searchQuery]);

  const sortedRows = useMemo(() => {
    if (!sortField) return filteredRows;

    const sorted = [...filteredRows];
    const directionMultiplier = sortDirection === "asc" ? 1 : -1;

    sorted.sort((left, right) => {
      let result = 0;

      switch (sortField) {
        case "date": {
          const leftTs = left.appointment.start
            ? dayjs(left.appointment.start).valueOf()
            : 0;
          const rightTs = right.appointment.start
            ? dayjs(right.appointment.start).valueOf()
            : 0;
          result = leftTs - rightTs;
          break;
        }
        case "patient":
          result = comparePtBrStrings(left.patientName, right.patientName);
          break;
        case "situation":
          result = comparePtBrStrings(
            getAppointmentSituationMeta(left.appointment).shortLabel,
            getAppointmentSituationMeta(right.appointment).shortLabel
          );
          break;
        case "location":
          result = comparePtBrStrings(left.location, right.location);
          break;
        case "service":
          result = comparePtBrStrings(left.service, right.service);
          break;
        case "practitioner":
          result = comparePtBrStrings(left.practitioner, right.practitioner);
          break;
        default:
          result = 0;
      }

      return result * directionMultiplier;
    });

    return sorted;
  }, [filteredRows, sortField, sortDirection]);

  useEffect(() => {
    setPage(0);
    setSelectedAppointmentIds(new Set());
  }, [searchQuery, practitionerFilter, locationFilter, subCaseFilter, sortField, sortDirection]);

  const showRowSelection = showAllActionIcons;
  const whatsAppDisabled =
    isMessagingTemplatesLoading || !hasMessagingTemplates;

  const allAppointmentIds = useMemo(
    () =>
      sortedRows
        .map((row) => row.appointment.id)
        .filter((id): id is string => Boolean(id)),
    [sortedRows]
  );

  const allRowsSelected =
    allAppointmentIds.length > 0 &&
    allAppointmentIds.every((id) => selectedAppointmentIds.has(id));
  const someRowsSelected = allAppointmentIds.some((id) =>
    selectedAppointmentIds.has(id)
  );

  const selectedRows = useMemo(
    () =>
      sortedRows.filter(
        (row) =>
          row.appointment.id &&
          selectedAppointmentIds.has(row.appointment.id)
      ),
    [sortedRows, selectedAppointmentIds]
  );

  const whatsAppRecipientSummary = useMemo(
    () =>
      summarizeMessagingRecipients(
        mapAppointmentRowsToMessagingPatientEntries(
          selectedRows,
          data?.patients ?? []
        )
      ),
    [selectedRows, data?.patients]
  );

  const selectedWhatsAppPatients = whatsAppRecipientSummary.uniquePatients;

  const selectedRescheduleEntries = useMemo(
    () =>
      buildRescheduleEntries(
        selectedRows.map((row) => row.appointment),
        data?.patients ?? []
      ),
    [selectedRows, data?.patients]
  );

  const rescheduleSelectionCount = selectedRescheduleEntries.length;

  const chargeResolveOptions = useMemo(
    () => ({
      slots: chargePaymentSlots,
      displaySchedules,
    }),
    [chargePaymentSlots, displaySchedules]
  );

  const selectedChargePayments = useMemo(
    () =>
      resolvePaymentDetailsForAppointments(
        selectedRows
          .filter((row) => row.appointment.status === "pending")
          .map((row) => row.appointment),
        data?.patients ?? [],
        pendingChargePayments,
        chargeResolveOptions
      ),
    [selectedRows, data?.patients, pendingChargePayments, chargeResolveOptions]
  );

  const handleBatchCharge = () => {
    if (selectedChargePayments.length === 0) return;
    setChargeConfirmOpen(true);
  };

  const confirmBatchCharge = () => {
    setChargeConfirmOpen(false);
    onBatchSendCharges?.(selectedChargePayments);
  };

  const chargeableSelectionCount = selectedChargePayments.length;
  const chargeableTotal = useMemo(
    () => sumPaymentDetailsAmount(selectedChargePayments),
    [selectedChargePayments]
  );

  const selectedChargePayment = useMemo(() => {
    if (!selectedAppointment) return null;
    return resolvePaymentDetailForPendingAppointment(
      selectedAppointment,
      data?.patients ?? [],
      pendingChargePayments,
      chargeResolveOptions
    );
  }, [
    selectedAppointment,
    data?.patients,
    pendingChargePayments,
    chargeResolveOptions,
  ]);

  const selectedChargeAmount = useMemo(
    () =>
      selectedChargePayment ? sumPaymentDetailsAmount([selectedChargePayment]) : 0,
    [selectedChargePayment]
  );

  const toggleAllRows = () => {
    if (allRowsSelected) {
      setSelectedAppointmentIds(new Set());
      return;
    }
    setSelectedAppointmentIds(new Set(allAppointmentIds));
  };

  const toggleRowSelection = (appointmentId: string) => {
    setSelectedAppointmentIds((current) => {
      const next = new Set(current);
      if (next.has(appointmentId)) next.delete(appointmentId);
      else next.add(appointmentId);
      return next;
    });
  };

  const handleBatchWhatsApp = () => {
    if (selectedRows.length === 1) {
      const row = selectedRows[0];
      const patient = (data?.patients ?? []).find((entry) =>
        row.appointment.participant?.some(
          (part) => part.actor?.reference === `Patient/${entry.id}`
        )
      );
      const messagingPatient = mapFhirPatientToMessagingPatient(patient);
      if (!messagingPatient) return;
      openWhatsAppSend({
        patient: messagingPatient,
        appointment: mapAppointmentToMessagingAppointment(row.appointment),
      });
      return;
    }
    if (whatsAppRecipientSummary.uniqueCount === 0) return;
    if (whatsAppRecipientSummary.duplicateEntries > 0) {
      setWhatsAppDupConfirmOpen(true);
      return;
    }
    openWhatsAppSendBatch({ patients: whatsAppRecipientSummary.uniquePatients });
  };

  const confirmBatchWhatsApp = () => {
    setWhatsAppDupConfirmOpen(false);
    openWhatsAppSendBatch({ patients: whatsAppRecipientSummary.uniquePatients });
  };

  const handleBatchReschedule = () => {
    if (rescheduleSelectionCount === 0) return;
    if (rescheduleSelectionCount === 1) {
      handleOpenReschedule(selectedRescheduleEntries[0].appointment);
      return;
    }
    setBatchRescheduleOpen(true);
  };

  const handleBatchRescheduleComplete = () => {
    refreshRows();
    onAppointmentsChanged?.();
    setSelectedAppointmentIds(new Set());
  };

  const hasMultipleSituations = useMemo(
    () =>
      hasMultipleAppointmentSituations(rows.map((row) => row.appointment)),
    [rows]
  );

  const showSubCaseFilter = subCaseFilterOptions && hasMultipleSituations;

  const whatsAppEnabledForSegment =
    crmMessagingEnabled || isMarcadasMode || isClosedMode;

  const canShowRescheduleForRow = (appointment: fhir5.Appointment) =>
    (isOverdueBookedMode || isMarcadasMode) &&
    canRescheduleAppointment(appointment);

  const canShowChargeForRow = (appointment: fhir5.Appointment) =>
    isMarcadasMode &&
    appointment.status === "pending" &&
    chargeActionsEnabled;

  const canShowWhatsAppForRow = () => whatsAppEnabledForSegment;

  const showActionsColumn =
    isOverdueBookedMode ||
    isMarcadasMode ||
    isClosedMode ||
    crmMessagingEnabled;

  const showBatchRescheduleAction =
    (isOverdueBookedMode || isMarcadasMode) && !isFulfilledMode && !isClosedMode;
  const showChargeAction = isMarcadasMode && chargeActionsEnabled;

  const whatsAppTooltip = isMessagingTemplatesLoading
    ? "Carregando modelos..."
    : hasMessagingTemplates
      ? "Enviar WhatsApp"
      : "Nenhum modelo de mensagem disponível";

  const emptyPeriodMessage = isOverdueBookedMode
    ? "Nenhuma consulta atrasada neste período"
    : isClosedMode
      ? "Nenhuma consulta cancelada ou sem pagamento neste período"
      : isMarcadasMode
        ? "Nenhuma próxima consulta neste período"
        : "Nenhuma consulta encontrada nesta situação";

  const paginatedRows = sortedRows.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const tableColSpan =
    (showActionsColumn ? 8 : 7) + (showRowSelection ? 1 : 0);

  useEffect(() => {
    if (!hasMultipleSituations && sortField === "situation") {
      resetSort("date");
    }
    if (!hasMultipleSituations && subCaseFilter !== "all") {
      setSubCaseFilter("all");
    }
  }, [hasMultipleSituations, sortField, subCaseFilter, resetSort]);

  const handleOpenReschedule = (appointment: any) => {
    setRescheduleAppointment(appointment);
    setRescheduleDialogOpen(true);
  };

  const handleOpenWhatsApp = (row: AppointmentTableRow) => {
    const patient = (data?.patients ?? []).find((entry) =>
      row.appointment.participant?.some(
        (part) => part.actor?.reference === `Patient/${entry.id}`
      )
    );
    const messagingPatient = mapFhirPatientToMessagingPatient(patient);
    if (!messagingPatient) return;
    openWhatsAppSend({
      patient: messagingPatient,
      appointment: mapAppointmentToMessagingAppointment(row.appointment),
    });
  };

  const handleOpenPaymentModal = (
    appointment: any,
    row?: { practitioner: string; location: string },
  ) => {
    const patient = (data?.patients ?? []).find((p) =>
      appointment.participant?.some(
        (part: any) => part.actor?.reference === `Patient/${p.id}`,
      ),
    );
    const patientPhone =
      patient?.telecom?.find((t: any) => t.system === "phone")?.value || "";

    setSelectedAppointment(appointment);
    setSelectedPaymentNoticeContext(
      row
        ? {
            practitionerName: row.practitioner,
            appointmentStart: appointment?.start,
            locationLabel: appointment?.virtualService?.[0]?.addressUrl
              ? "Teleconsulta"
              : row.location !== "—"
                ? row.location
                : "local não informado",
            patientName: row.patientName,
            patientPhone,
          }
        : null,
    );
    setPaymentModalOpen(true);
    setErrorSendCharge(undefined);
    setLoadingSendCharge(false);
    setLoadingConfirmPayment(false);
  };

  const handleSendCharge = async () => {
    if (!selectedAppointment) return;
    setLoadingSendCharge(true);
    setErrorSendCharge(undefined);
    try {
      const patient = (data?.patients ?? []).find((p) =>
        selectedAppointment.participant?.some(
          (part: any) => part.actor?.reference === `Patient/${p.id}`
        )
      );

      const practitioner = selectedAppointment.participant?.find((p: any) =>
        p.actor?.reference?.startsWith("Practitioner/")
      );

      const service = selectedAppointment.participant?.find((p: any) =>
        p.actor?.reference?.startsWith("HealthcareService/")
      );

      const paymentLink = await marketplaceService.sendPaymentMessage({
        slotReference: selectedAppointment?.slot?.[0]?.reference || "",
        patientName: patient?.name?.[0]?.text || "",
        healthcareDisplay: service?.actor?.display || "",
        practitionerName: practitioner?.actor?.display || "",
        phoneNumber:
          patient?.telecom?.find((t: any) => t.system === "phone")?.value || "",
      });

      await notificationService.sendNotification(
        buildCobrancaConsultaNotification({
          cpf: patient?.identifier?.[0]?.value || "",
          nomePaciente: patient?.name?.[0]?.text || "",
          tipoConsulta: service?.actor?.display || "",
          nomeProfissional: practitioner?.actor?.display || "",
          linkPagamento: paymentLink,
          organizationId: access?.organizationId,
        }),
        { organizationId: access?.organizationId },
      );

      const wasAlreadySent = isChargeNotificationSentOnAppointment(
        selectedAppointment
      );
      await markAppointmentChargeNotificationSent(selectedAppointment);
      if (!wasAlreadySent && !keepOpenAfterCharge) refreshRows();

      toast.success("Cobrança enviada com sucesso!");
      if (!keepOpenAfterCharge) setPaymentModalOpen(false);
    } catch (chargeError: any) {
      if (chargeError?.response?.status === 404) {
        setErrorSendCharge(
          "Não é possível enviar cobrança para consulta em agenda sem custo."
        );
      } else {
        setErrorSendCharge("Erro ao gerar link de pagamento. Tente novamente.");
      }
      throw chargeError;
    } finally {
      setLoadingSendCharge(false);
    }
  };

  const handleConfirmPayment = async () => {
    if (!selectedAppointment) return;
    setLoadingConfirmPayment(true);
    try {
      await EditAppointment({ ...selectedAppointment, status: "booked" });
      refreshRows();
      setPaymentModalOpen(false);
    } catch {
      toast.error("Erro ao confirmar pagamento");
    } finally {
      setLoadingConfirmPayment(false);
    }
  };

  return (
    <>
      <Stack spacing={2} sx={{ width: "100%", maxWidth: "100%", minWidth: 0 }}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={1.5}
          alignItems={{ xs: "stretch", md: "center" }}
        >
          <TextField
            size="medium"
            fullWidth
            placeholder="Buscar paciente, profissional, local ou serviço"
            sx={{ "& .MuiInputBase-input": { fontSize: "1.05rem" } }}
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />

          <FormControl
            size="medium"
            sx={{
              minWidth: { xs: "100%", md: 200 },
              "& .MuiInputBase-input": { fontSize: "1.05rem" },
              "& .MuiInputLabel-root": { fontSize: "1rem" },
            }}
          >
            <InputLabel id="appointments-table-practitioner-label">
              Profissional
            </InputLabel>
            <Select
              labelId="appointments-table-practitioner-label"
              label="Profissional"
              value={practitionerFilter}
              onChange={(event) => setPractitionerFilter(event.target.value)}
            >
              <MenuItem value="all">Todos</MenuItem>
              {practitionerOptions.map(([key, name]) => (
                <MenuItem key={key} value={key}>
                  {name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl
            size="medium"
            sx={{
              minWidth: { xs: "100%", md: 200 },
              "& .MuiInputBase-input": { fontSize: "1.05rem" },
              "& .MuiInputLabel-root": { fontSize: "1rem" },
            }}
          >
            <InputLabel id="appointments-table-location-label">Local</InputLabel>
            <Select
              labelId="appointments-table-location-label"
              label="Local"
              value={locationFilter}
              onChange={(event) => setLocationFilter(event.target.value)}
            >
              <MenuItem value="all">Todos</MenuItem>
              {locationOptions.map(([key, name]) => (
                <MenuItem key={key} value={key}>
                  {name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {showSubCaseFilter && (
            <FormControl
              size="medium"
              sx={{
                minWidth: { xs: "100%", md: 240 },
                "& .MuiInputBase-input": { fontSize: "1.05rem" },
                "& .MuiInputLabel-root": { fontSize: "1rem" },
              }}
            >
              <InputLabel id="appointments-table-subcase-label">
                Situação
              </InputLabel>
              <Select
                labelId="appointments-table-subcase-label"
                label="Situação"
                value={subCaseFilter}
                onChange={(event) =>
                  setSubCaseFilter(event.target.value as AppointmentSubCaseFilter)
                }
              >
                {subCaseFilterOptions!.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Stack>

        {showRowSelection && sortedRows.length > 0 && (
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            alignItems={{ xs: "stretch", sm: "center" }}
            justifyContent="space-between"
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <Checkbox
                checked={allRowsSelected}
                indeterminate={someRowsSelected && !allRowsSelected}
                onChange={toggleAllRows}
                disabled={allAppointmentIds.length === 0}
              />
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{ fontSize: "1.02rem" }}
              >
                Selecionar todas ({allAppointmentIds.length}{" "}
                {allAppointmentIds.length === 1 ? "consulta" : "consultas"})
                {isMarcadasMode && someRowsSelected && (
                  <>
                    {" · "}
                    {chargeableSelectionCount} sem pagamento
                  </>
                )}
              </Typography>
            </Stack>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
              {showBatchRescheduleAction && (
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<EditCalendar />}
                  disabled={rescheduleSelectionCount === 0}
                  onClick={handleBatchReschedule}
                  sx={{ alignSelf: { xs: "stretch", sm: "auto" } }}
                >
                  Reagendar todas ({rescheduleSelectionCount})
                </Button>
              )}
              {isMarcadasMode && showChargeAction && (
                <Button
                  variant="contained"
                  startIcon={<AttachMoney />}
                  disabled={
                    !chargeActionsEnabled ||
                    chargeableSelectionCount === 0 ||
                    loadingBatchCharge
                  }
                  onClick={handleBatchCharge}
                  sx={{ alignSelf: { xs: "stretch", sm: "auto" } }}
                >
                  Enviar cobranças ({chargeableSelectionCount})
                </Button>
              )}
              <Button
                variant="contained"
                color="success"
                startIcon={<WhatsApp />}
                disabled={
                  selectedWhatsAppPatients.length === 0 || whatsAppDisabled
                }
                onClick={handleBatchWhatsApp}
                sx={{ alignSelf: { xs: "stretch", sm: "auto" } }}
              >
                Enviar WhatsApp ({selectedWhatsAppPatients.length})
              </Button>
            </Stack>
          </Stack>
        )}

        <TableContainer
          component={Paper}
          variant="outlined"
          sx={statsTableContainerSx}
        >
        <Table size="medium" sx={statsTableSx}>
          <TableHead>
            <TableRow>
              {showRowSelection && (
                <TableCell padding="checkbox" width={48} sx={statsTableHeadCellSx} />
              )}
              <StatsSortableTableHeadCell
                label="Data"
                field="date"
                sortField={sortField}
                sortDirection={sortDirection}
                onSort={requestSort}
                width={118}
              />
              <TableCell sx={{ ...statsTableHeadCellSx, ...statsTableTimeCellSx }}>
                Hora
              </TableCell>
              {hasMultipleSituations ? (
                <StatsSortableTableHeadCell
                  label="Situação"
                  field="situation"
                  sortField={sortField}
                  sortDirection={sortDirection}
                  onSort={requestSort}
                  align="center"
                  width={158}
                />
              ) : (
                <TableCell
                  align="center"
                  sx={{ ...statsTableHeadCellSx, ...statsTableSituationCellSx }}
                >
                  Situação
                </TableCell>
              )}
              <StatsSortableTableHeadCell
                label="Paciente"
                field="patient"
                sortField={sortField}
                sortDirection={sortDirection}
                onSort={requestSort}
                width="26%"
              />
              <StatsSortableTableHeadCell
                label="Local"
                field="location"
                sortField={sortField}
                sortDirection={sortDirection}
                onSort={requestSort}
                width="13%"
              />
              <StatsSortableTableHeadCell
                label="Serviço"
                field="service"
                sortField={sortField}
                sortDirection={sortDirection}
                onSort={requestSort}
                width="13%"
              />
              <StatsSortableTableHeadCell
                label="Profissional"
                field="practitioner"
                sortField={sortField}
                sortDirection={sortDirection}
                onSort={requestSort}
                width="13%"
              />
              {showActionsColumn && (
                <TableCell
                  align="center"
                  sx={{ ...statsTableHeadCellSx, width: 132 }}
                >
                  Ações
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={tableColSpan} align="center" sx={{ py: 4 }}>
                  <CircularLoading />
                </TableCell>
              </TableRow>
            ) : error ? (
              <TableRow>
                <TableCell colSpan={tableColSpan} align="center" sx={{ py: 3 }}>
                  <Typography color="error" sx={statsTableCellTextSx}>
                    Não foi possível carregar. Tente novamente.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={tableColSpan} align="center" sx={{ py: 3 }}>
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{ ...statsTableCellTextSx, fontSize: "1.05rem" }}
                  >
                    {emptyPeriodMessage}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : sortedRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={tableColSpan} align="center" sx={{ py: 3 }}>
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={statsTableCellTextSx}
                  >
                    Nenhum resultado para os filtros aplicados
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              paginatedRows.map((row) => {
              const isPending = row.appointment?.status === "pending";
              const notified =
                isPending &&
                isChargeNotificationSentOnAppointment(row.appointment);

              return (
                <TableRow
                  key={row.appointment.id}
                  hover
                  selected={
                    !!row.appointment.id &&
                    selectedAppointmentIds.has(row.appointment.id)
                  }
                >
                  {showRowSelection && (
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={
                          !!row.appointment.id &&
                          selectedAppointmentIds.has(row.appointment.id)
                        }
                        disabled={!row.appointment.id}
                        onChange={() => {
                          if (row.appointment.id) {
                            toggleRowSelection(row.appointment.id);
                          }
                        }}
                      />
                    </TableCell>
                  )}
                  <TableCell sx={statsTableDateCellSx}>
                    <Typography sx={statsTableDateTextSx}>{row.date}</Typography>
                  </TableCell>
                  <TableCell sx={statsTableTimeCellSx}>
                    <Typography sx={statsTableTimeTextSx}>{row.time}</Typography>
                  </TableCell>
                  <TableCell align="center" sx={statsTableSituationCellSx}>
                    <ConsultationSituationCell
                      meta={getAppointmentSituationMeta(row.appointment)}
                      compact
                    />
                  </TableCell>
                  <TableCell sx={statsTablePatientCellSx}>
                    <Tooltip title={row.patientName}>
                      <Typography fontWeight={600} sx={statsTableCellTextSx} noWrap>
                        {row.patientName}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell sx={statsTableTruncatedCellSx}>
                    <Tooltip title={row.location}>
                      <Typography color="text.secondary" sx={statsTableCellTextSx} noWrap>
                        {row.location}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell sx={statsTableTruncatedCellSx}>
                    <Tooltip title={row.service}>
                      <Typography color="text.secondary" sx={statsTableCellTextSx} noWrap>
                        {row.service}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell sx={statsTableTruncatedCellSx}>
                    <Tooltip title={row.practitioner}>
                      <Typography color="text.secondary" sx={statsTableCellTextSx} noWrap>
                        {row.practitioner}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  {showActionsColumn && (
                    <TableCell align="center">
                      <Stack
                        direction="row"
                        justifyContent="center"
                        spacing={0.25}
                      >
                        {canShowRescheduleForRow(row.appointment) && (
                          <Tooltip title="Reagendar">
                            <IconButton
                              size="medium"
                              color="primary"
                              sx={statsTableActionButtonSx}
                              onClick={() =>
                                handleOpenReschedule(row.appointment)
                              }
                            >
                              <EditCalendar sx={statsTableActionIconSx} />
                            </IconButton>
                          </Tooltip>
                        )}
                        {canShowChargeForRow(row.appointment) && (
                          <Tooltip
                            title={
                              notified ? "Reenviar cobrança" : "Enviar cobrança"
                            }
                          >
                            <IconButton
                              size="medium"
                              sx={{
                                ...statsTableActionButtonSx,
                                color: "primary800.main",
                                position: "relative",
                              }}
                              onClick={() =>
                                handleOpenPaymentModal(row.appointment, row)
                              }
                            >
                              <AttachMoney sx={statsTableActionIconSx} />
                              {notified && (
                                <Check
                                  sx={{
                                    fontSize: 14,
                                    color: "success700.main",
                                    position: "absolute",
                                    right: 0,
                                    top: 0,
                                  }}
                                />
                              )}
                            </IconButton>
                          </Tooltip>
                        )}
                        {canShowWhatsAppForRow() && (
                          <Tooltip title={whatsAppTooltip}>
                            <IconButton
                              size="medium"
                              color="primary"
                              sx={statsTableActionButtonSx}
                              onClick={() => handleOpenWhatsApp(row)}
                            >
                              <WhatsApp sx={statsTableActionIconSx} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                  )}
                </TableRow>
              );
            })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={loading ? 0 : sortedRows.length}
        page={page}
        onPageChange={(_, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(event) => {
          setRowsPerPage(parseInt(event.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={[8, 16, 32]}
        labelRowsPerPage="Exibir"
        labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
        sx={{
          ".MuiTablePagination-select": { mr: 1 },
          ".MuiTablePagination-selectLabel": { mr: 1 },
          "& .MuiTablePagination-displayedRows, & .MuiTablePagination-selectLabel": {
            fontSize: "1rem",
          },
        }}
      />
      </Stack>

      {rescheduleDialogOpen && rescheduleAppointment && (
        <ScheduleRescheduleDialog
          isOpen={rescheduleDialogOpen}
          setIsOpen={setRescheduleDialogOpen}
          appointment={rescheduleAppointment}
          setRefetch={() => {
            refreshRows();
            onAppointmentsChanged?.();
          }}
        />
      )}

      <SendWhatsAppMessageDialog {...sendDialogProps} />

      <BatchChargeConfirmDialog
        open={chargeConfirmOpen}
        onClose={() => setChargeConfirmOpen(false)}
        onConfirm={confirmBatchCharge}
        loading={loadingBatchCharge}
        totalSelected={selectedRows.length}
        chargeableCount={chargeableSelectionCount}
        chargeableTotal={chargeableTotal}
      />

      <BatchWhatsAppDuplicateConfirmDialog
        open={whatsAppDupConfirmOpen}
        onClose={() => setWhatsAppDupConfirmOpen(false)}
        onConfirm={confirmBatchWhatsApp}
        totalEntries={whatsAppRecipientSummary.totalEntries}
        uniqueCount={whatsAppRecipientSummary.uniqueCount}
        duplicateEntries={whatsAppRecipientSummary.duplicateEntries}
      />

      <BatchRescheduleDialog
        open={batchRescheduleOpen}
        onClose={() => setBatchRescheduleOpen(false)}
        appointments={selectedRows.map((row) => row.appointment)}
        patients={data?.patients ?? []}
        onComplete={handleBatchRescheduleComplete}
      />

      {paymentModalOpen && (
        <PaymentPendingModal
          isOpen={paymentModalOpen}
          onClose={() => setPaymentModalOpen(false)}
          loadingSendCharge={loadingSendCharge}
          errorSendCharge={errorSendCharge}
          hideConfirmPayment
          loadingConfirmPayment={loadingConfirmPayment}
          chargeAmount={selectedChargeAmount > 0 ? selectedChargeAmount : undefined}
          onSendCharge={handleSendCharge}
          onConfirmPayment={handleConfirmPayment}
          appointment={selectedAppointment}
          notificationSent={isChargeNotificationSentOnAppointment(
            selectedAppointment
          )}
          whatsappNoticeContext={selectedPaymentNoticeContext ?? undefined}
        />
      )}
    </>
  );
}
